import React, { useCallback, useEffect, useState } from 'react'
import { Grid, LinearProgress } from '@mui/material'
import { useFormContext, UseFormSetValue } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import useAuth from '../../../../hooks/useAuth'
import IssueApi from '../../../../api/IssueApi'
import IssuesGroupApi from '../../../../api/IssuesGroupApi'
import ConvertiblesApi from '../../../../api/ConvertiblesApi'
import { AllCompanyIssues } from './AllCompanyIssues'
import { IssueEntity } from 'types/IssueEntity'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import { IssuesGroup } from 'types/IssuesGroupEntity'
import { IssueGroups } from './IssueGroups'
import { FormIssueEntity } from '../../types/FormIssueEntity'
import { FormConvertibleEntity } from '../../types/FormConvertibleEntity'

const useSetFormGroups = (setValue: UseFormSetValue<FormIssueEntity | FormConvertibleEntity>) => {
    const setFormGroups = useCallback(
        (groups: IssuesGroup[]) => {
            setValue('issueGroups', groups)
        },
        [setValue], // Add setValue as a dependency
    )

    return { setFormGroups }
}

const issueApi = new IssueApi()
const convertibleApi = new ConvertiblesApi()
const issuesGroupApi = new IssuesGroupApi()

function LinkedIssuesDetails({
    entity,
    entityType,
    setIsCustomFormDirty,
}: {
    setIsCustomFormDirty: (v: boolean) => void
} & (
    | {
          entityType: 'issue'
          entity: FormIssueEntity
      }
    | {
          entityType: 'convertible'
          entity: ConvertibleEntity
      }
)) {
    const { isAuthenticated } = useAuth()
    const { setValue } = useFormContext<FormIssueEntity | FormConvertibleEntity>()
    const { setFormGroups } = useSetFormGroups(setValue)

    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [convertibles, setConvertibles] = useState<ConvertibleEntity[] | null>(null)
    const [issueGroups, setIssueGroups] = useState<FormIssueEntity['issueGroups'] | null>(null)
    const companyId = entity.companyId

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            let fetchedIssues = await issueApi.findByCompanyId(companyId, false)
            fetchedIssues = fetchedIssues.filter((i) => i.id !== entity?.id)
            setIssues(fetchedIssues)
        }
        void fetchData()
    }, [isAuthenticated, companyId, entity])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            let fetchedConvertibles = await convertibleApi.findByCompanyId(companyId)
            fetchedConvertibles = fetchedConvertibles.filter((c) => c.id !== entity.id)
            setConvertibles(fetchedConvertibles)
        }
        void fetchData()
    }, [isAuthenticated, companyId, entity.id])

    useEffect(() => {
        async function fetchData() {
            if (!isAuthenticated || !companyId) return
            const issueGroups = await issuesGroupApi.findByCompanyId(companyId)
            setFormGroups(issueGroups)
            setIssueGroups(issueGroups)
        }
        void fetchData()
    }, [setIssueGroups, setFormGroups, isAuthenticated, companyId])

    if (!issues || !issueGroups) return <LinearProgress />

    const handleCreateGroup = (entities: Array<ConvertibleEntity | IssueEntity>) => {
        const groupId = uuidv4()
        const entitiesInGroup = [entity, ...entities].filter((e) => e !== null)
        const issues = entitiesInGroup.filter((e) => e.id && 'type' in e) as IssueEntity[]
        const convertibles = entitiesInGroup.filter((e) => e.id && !('type' in e)) as ConvertibleEntity[]

        const newGroups = [
            ...issueGroups,
            {
                groupId,
                convertibles,
                issues,
            },
        ]
        setFormGroups(newGroups)
        setIssueGroups(newGroups)
        setIsCustomFormDirty(true)
    }
    const handleAddToGroup = (groupId: string) => {
        const newIssueGroups = issueGroups.filter((ig) => ig.groupId !== groupId)
        const group = issueGroups.find((ig) => ig.groupId === groupId)

        if (!group) {
            console.error('handleAddToGroup: Failed to find group')

            return
        }

        if (!entity.id) {
            console.error('Cant add an issue without id')
            return
        }

        switch (entityType) {
            case 'convertible':
                {
                    const g = { ...entity, convertibleId: entity.id, issueId: null }
                    newIssueGroups.push({
                        ...group,
                        convertibles: [...group.convertibles, g],
                    })
                }
                break
            case 'issue':
                {
                    const g = {
                        ...group,
                        issues: [
                            ...group.issues,
                            { ...entity, id: entity.id, issueId: entity.id, convertibleId: null },
                        ],
                    }
                    newIssueGroups.push(g)
                }
                break
            default:
                newIssueGroups.push(group)
        }

        setFormGroups(newIssueGroups)
        setIssueGroups(newIssueGroups)
        setIsCustomFormDirty(true)
    }
    const handleDeleteFromGroup = (groupId: string) => {
        const newIssueGroups = issueGroups.filter((ig) => ig.groupId !== groupId)
        const group = issueGroups.find((ig) => ig.groupId === groupId)
        if (!group) {
            console.error('handleDeleteFromGroup: Group not found')
            return
        }

        if ([...group.issues, ...group.convertibles].length <= 2) {
            // delete group if only two issues left
            setFormGroups(newIssueGroups)
            setIssueGroups(newIssueGroups)
            return
        }

        switch (entityType) {
            case 'convertible':
                newIssueGroups.push({ ...group, convertibles: group.convertibles.filter((c) => c.id !== entity.id) })
                break
            case 'issue':
                newIssueGroups.push({ ...group, issues: group.issues.filter((i) => i.id !== entity.id) })
                break
            default:
                newIssueGroups.push(group)
        }

        setFormGroups(newIssueGroups)
        setIssueGroups(newIssueGroups)
        setIsCustomFormDirty(true)
    }

    const isGuarantorFeeIssue = entityType === 'issue' && entity.type === 'Guarantor fee issue'
    const hasReachedMaxIssueGroups =
        isGuarantorFeeIssue && issueGroups.some((g) => g.issues.some((e) => e.id === entity.id))

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={6}>
                    <AllCompanyIssues
                        handleCreateGroup={handleCreateGroup}
                        hasReachedMaxIssueGroups={hasReachedMaxIssueGroups}
                        issues={issues}
                        convertibles={convertibles}
                        isGuarantorFeeIssue={isGuarantorFeeIssue}
                        isIssuePage={entityType === 'issue'}
                    />
                </Grid>
                <Grid size={6}>
                    <IssueGroups
                        entity={entity}
                        issueGroups={issueGroups}
                        handleDeleteFromGroup={handleDeleteFromGroup}
                        handleAddToGroup={handleAddToGroup}
                        isGuarantorFeeIssue={isGuarantorFeeIssue}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default LinkedIssuesDetails
